import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { paths, type AppMessage } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";

const severityStyle: Record<AppMessage["severity"], string> = {
  info: "bg-ink text-white",
  success: "bg-success text-white",
  warning: "bg-brandorange text-white",
  error: "bg-danger text-white",
};

// Service-notice banners. `context` decides which audience renders here.
export function MessageBanner({ context, tenantId }: { context: "app" | "login"; tenantId?: string | null }) {
  const [messages, setMessages] = useState<Array<AppMessage & { id: string }>>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const q = query(collection(db, paths.appMessages()), where("active", "==", true));
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AppMessage) })));
    });
  }, []);

  const visible = messages.filter((m) => {
    if (dismissed.has(m.id)) return false;
    if (context === "login") return m.audience === "login";
    if (m.audience === "all") return true;
    if (m.audience === "tenants") return !!tenantId && m.tenantIds.includes(tenantId);
    return false;
  });

  if (visible.length === 0) return null;

  return (
    <div>
      {visible.map((m) => (
        <div key={m.id} className={cn("flex items-start justify-between gap-3 px-4 py-2 text-sm", severityStyle[m.severity])}>
          <div>
            <span className="font-semibold">{m.title}</span>
            {m.body ? <span className="ml-2 opacity-90">{m.body}</span> : null}
            {m.ctaUrl ? (
              <a href={m.ctaUrl} className="ml-2 underline" target="_blank" rel="noreferrer">
                {m.ctaLabel || "Learn more"}
              </a>
            ) : null}
          </div>
          <button
            className="shrink-0 opacity-70 hover:opacity-100"
            onClick={() => setDismissed(new Set([...dismissed, m.id]))}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
